import { jwtDecode } from "jwt-decode";
import { useState } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import { Link } from "react-router-dom";
import logo from "../assets/logo.png"; // Adjust the path to your logo
import Loader from "../components/Loader";
import { RECAPTCHA_SITE_KEY } from "../config";
import Api from "../service/http";

const SigninForm = () => {
  const params = new URLSearchParams(window.location.search);
  const redirect = params.get("redirect");
  const tag = params.get("tag");

  const [isLoggedIn, setIsLoggedIn] = useState(
    localStorage.getItem("accessToken") ? true : false
  );
  const [loading, setLoading] = useState(false);

  const [captchaToken, setCaptchaToken] = useState(null);
  const handleCaptchaChange = (token) => {
    setCaptchaToken(token);
  };

  if (isLoggedIn) {
    let url = "/dashboard";
    if (redirect) {
      url = redirect;
      if (tag) {
        url += `#${tag}`;
      }
    }
    window.location.href = url;
  }

  const [formData, setFormData] = useState({
    username: "",
    password: "",
    remember: true,
    showPassword: false,
  });
  const [error, setError] = useState("");

  const handleChange = (e) => {
    const { name, type, checked, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const togglePasswordVisibility = () => {
    setFormData((prev) => ({
      ...prev,
      showPassword: !prev.showPassword,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    await handleLogin({ ...formData, type: "manual" });
  };

  const handleLogin = async (data) => {
    if (!captchaToken) {
      alert("Please verify reCAPTCHA.");
      return;
    }
    setLoading(true);
    const response = await Api.post("/web/login", data);
    if (response.status === 200) {
      localStorage.clear();
      localStorage.setItem("accessToken", JSON.stringify(response.data.access));
      localStorage.setItem("user", JSON.stringify(response.data.user));
      let url = "/dashboard";
      if (redirect) {
        url = redirect;
        if (tag) {
          url += `#${tag}`;
        }
      }
      window.location.href = url;
    } else {
      setError(response?.data?.message || "Login failed");
      console.error("Login failed");
    }
    setLoading(false);
  };

  const handleFacebookLogin = async (response) => {
    if (!captchaToken) {
      alert("Please verify reCAPTCHA.");
      return;
    }

    if (!response.userID) {
      console.log("Facebook login failed");
      return;
    }

    handleLogin({
      id: response?.userID,
      email: response?.email,
      // dob: response?.birthday,
      name: response?.name,
      photo: response?.picture?.data?.url,
      type: "facebook",
    });
  }

  const handleGoogleLogin = async (credentialResponse) => {
    if (!captchaToken) {
      alert("Please verify reCAPTCHA.");
      return;
    }
    const decoded = jwtDecode(credentialResponse.credential);
    handleLogin({
      id: decoded.sub,
      email: decoded.email,
      name: decoded.name,
      photo: decoded.picture,
      type: "google",
    });
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50 px-4 py-12">
      <div className="w-full max-w-md bg-white rounded-2xl p-8 shadow-xl border border-gray-100 text-gray-900 text-center">
        {/* Icon */}
        <div className="w-20 h-20 mx-auto mb-6 flex items-center justify-center rounded-2xl bg-indigo-50 shadow-inner">
          <img src={logo} alt="Logo" className="h-12" />
        </div>

        <h2 className="text-3xl font-bold mb-2 text-gray-900">Welcome Back</h2>
        <p className="text-gray-500 mb-8">Please enter your details to sign in</p>

        {error && (
          <div className="mb-6 p-3 bg-red-50 text-red-600 rounded-lg text-sm font-medium border border-red-100">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-5 text-left">
          {/* Email */}
          <div>
            <label className="block mb-1.5 text-sm font-semibold text-gray-700">Email Address</label>
            <input
              type="email"
              name="username"
              value={formData.username}
              onChange={handleChange}
              placeholder="name@company.com"
              className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all placeholder:text-gray-400"
              required
            />
          </div>

          {/* Password */}
          <div>
            <label className="block mb-1.5 text-sm font-semibold text-gray-700">Password</label>
            <div className="relative">
              <input
                type={formData.showPassword ? "text" : "password"}
                name="password"
                value={formData.password}
                onChange={handleChange}
                placeholder="••••••••"
                className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all placeholder:text-gray-400"
                required
              />
              <button
                type="button"
                onClick={togglePasswordVisibility}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
              >
                {formData.showPassword ? (
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M3.707 2.293a1 1 0 00-1.414 1.414l14 14a1 1 0 001.414-1.414l-1.473-1.473A10.014 10.014 0 0019.542 10C18.268 5.943 14.478 3 10 3a9.958 9.958 0 00-4.512 1.074l-1.78-1.781zm4.261 4.26a4 4 0 015.493 5.493L8.263 6.846l-.295-.293zM4.62 7.757L3.043 6.18A10.116 10.116 0 00.458 10c1.274 4.057 5.065 7 9.542 7 .847 0 1.669-.105 2.454-.303L10.88 15.08a4 4 0 01-5.438-5.437l-.822-.826z" clipRule="evenodd" />
                  </svg>
                ) : (
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                    <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
                    <path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd" />
                  </svg>
                )}
              </button>
            </div>
          </div>

          {/* Remember & Forgot */}
          <div className="flex items-center justify-between text-sm">
            <label className="flex items-center cursor-pointer group">
              <input
                type="checkbox"
                name="remember"
                checked={formData.remember}
                onChange={handleChange}
                className="w-4 h-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 cursor-pointer"
              />
              <span className="ml-2 text-gray-600 group-hover:text-gray-900 transition-colors">Remember me</span>
            </label>
            <Link
              to="/forget-password"
              className="font-medium text-indigo-600 hover:text-indigo-500 transition-colors"
            >
              Forgot password?
            </Link>
          </div>

          {/* Submit Button */}
          <div className="flex flex-col items-center gap-4 pt-2">
            <div className="flex items-center justify-center overflow-hidden rounded-xl border border-gray-100 bg-gray-50/50 w-full" style={{ transform: "scale(0.9)" }}>
              <ReCAPTCHA
                sitekey={RECAPTCHA_SITE_KEY}
                onChange={handleCaptchaChange}
                size="normal"
              />
            </div>
            
            <button
              type="submit"
              className="w-full bg-indigo-600 hover:bg-indigo-700 active:scale-[0.98] transition-all text-white h-[52px] rounded-xl font-semibold shadow-lg shadow-indigo-200 disabled:opacity-70 disabled:active:scale-100 flex items-center justify-center gap-2"
              disabled={loading}
            >
              {loading ? (
                <>
                  <Loader size="xs" />
                  <span>Signing in...</span>
                </>
              ) : (
                "Sign in"
              )}
            </button>
          </div>

          {/* <div className="flex items-center justify-center mt-4">
            <div>
              <div className="flex items-center justify-center">
                <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
                  <GoogleLoginButton handleLogin={handleGoogleLogin} />
                </GoogleOAuthProvider>
              </div>

              <FacebookLoginButton handleLogin={handleFacebookLogin} />
            </div>
          </div> */}

          {/* Sign up link */}
          {/* <p className="text-center text-sm mt-2">
            Looking to{" "}
            <Link to="/signup" className="text-blue-400 underline">
              Sign up
            </Link>
          </p> */}
        </form>
      </div>
    </div>
  );
};

export default SigninForm;
